// Pure geometry for the timeline. No canvas, no React, no fetch -- everything here is a
// function of numbers, which is what makes the zoom and pan behaviour testable without a
// browser. The renderer and the pointer handlers both go through these.
//
// Split across ./geometry/: the view window and its arithmetic, the request windows and the
// gridline ladder derived from them, and the blocks placed into pixel space. What is left
// here is where the archive ends, which is the one question all three defer to.

import { LIVE_EDGE_FLUSH_MS } from "./geometry/blocks";
import type { View } from "./geometry/view";

export * from "./geometry/view";
export * from "./geometry/blocks";
export * from "./geometry/window";

/**
 * The newest instant the recording reaches.
 *
 * The clock while the recorder is keeping up with it, and the end of the recording once it is
 * not. The newest recorded instant always trails the clock a little -- see `LIVE_EDGE_FLUSH_MS`
 * for by how much and why -- and a run whose tail has not been written out yet is not a hole in
 * the archive. Past the flush window the recorder really has stopped, and the stretch in front
 * of it is a hole, drawn as one, so the data ends where it ends.
 *
 * A newest run ahead of the clock wins outright, so a skewed server can never make its own
 * newest audio unreachable.
 *
 * Where the audio ends is not where playback may begin -- for that, see `playableEnd`.
 */
export function archiveEnd(latestMs: number | null, nowMs: number): number {
  if (latestMs === null) return nowMs;
  if (latestMs >= nowMs) return latestMs;
  return nowMs - latestMs <= LIVE_EDGE_FLUSH_MS ? nowMs : latestMs;
}

/**
 * The newest instant a playlist will actually serve.
 *
 * The archive publishes its live edge a fixed distance behind the clock, because the recorder
 * is still appending to the newest hour file and the last few seconds are bytes it has written
 * but not flushed. `delayMs` is that hold-back, served as `live_delay_seconds` rather than
 * guessed here: it is the server's own `LIVE_SAFETY_MARGIN`, and a copy of it in this file
 * would be wrong the first time the deployment changed it, silently and in the direction that
 * hands the player a playlist over nothing.
 *
 * It is the whole story, and only the server's. How far behind the clock this tab's own audio
 * element happens to be sitting is a fact about that connection's own backlog, not about what
 * the archive will hand over. Folding it in here made the same instant playable with a show
 * playing and "not yet" while listening live, over a stretch the archive serves either way.
 *
 * The hold-back applies only while the recorder is keeping up. Once it has stopped, the end of
 * the recording is already older than the hold-back and is the honest wall -- subtracting from
 * a dead edge would make the last seconds of a finished archive permanently unreachable.
 */
export function playableEnd(latestMs: number | null, nowMs: number, delayMs: number): number {
  const end = archiveEnd(latestMs, nowMs);
  return end === nowMs ? nowMs - Math.max(0, delayMs) : end;
}

/**
 * Where the live delay begins, or null when there is not one on screen to speak of.
 *
 * The stretch between `playableEnd` and the clock: audio that is being recorded this second
 * and that no playlist will hand over yet. It gets its own marking rather than the gap hatch
 * because it is the opposite claim -- a gap is audio that was never recorded and never will be,
 * this is audio arriving. It is also the only honest answer to why the playhead stops short of
 * a ruler that is a clock.
 *
 * Null once the recorder has stopped: then the wall is the end of the recording, the hole in
 * front of it is a real one, and the hatch already says so. Two markings over the same stretch
 * making different claims would be worse than either.
 */
export function liveDelayFromMs(
  latestMs: number | null,
  nowMs: number,
  delayMs: number,
): number | null {
  if (archiveEnd(latestMs, nowMs) !== nowMs) return null;
  return delayMs > 0 ? nowMs - delayMs : null;
}

/**
 * Clamp the view so the playhead cannot be scrolled off the end of the archive.
 *
 * The centre line is the playhead, so a bound on the centre is a bound on where the playhead
 * may be parked, and the two ends of the data are walls. No slack outside them: a playhead
 * parked past the wall sits somewhere nothing can be played from. The emptiness that softens
 * the stop says "this is the end" instead: the viewport reaches half its own width past the
 * centre, so the wall shows a half-screen of it.
 *
 * An unknown end places no wall. A window that found no runs says nothing about where the
 * archive begins, and the fetch overscan means the loaded window's ends recede as it is panned
 * -- these only stop being provisional at the ends of the archive itself.
 */
export function clampToBounds(
  view: View,
  earliestMs: number | null,
  latestMs: number | null,
  nowMs: number,
  delayMs: number,
): View {
  let centerMs = view.centerMs;
  if (earliestMs !== null) centerMs = Math.max(centerMs, earliestMs);
  centerMs = Math.min(centerMs, playableEnd(latestMs, nowMs, delayMs));
  return centerMs === view.centerMs ? view : { ...view, centerMs };
}

/**
 * Whether an instant is somewhere the archive could hand over audio, and so somewhere a click
 * may land.
 *
 * The same interval `clampToBounds` holds the playhead inside, asked about an arbitrary x
 * rather than the centre. It has to be the same one: the emptiness a viewer can see past the
 * ends is scenery, and asking for a playlist over it gets a 404 back -- an error message for
 * clicking on nothing. The live delay is scenery of the same kind. It is drawn, so it does not
 * look like nothing, but it is a stretch the recorder has not finished with and a landing there
 * would be the same 404 with a more confusing cause.
 */
export function withinArchive(
  ms: number,
  earliestMs: number | null,
  latestMs: number | null,
  nowMs: number,
  delayMs: number,
): boolean {
  if (earliestMs !== null && ms < earliestMs) return false;
  return ms < playableEnd(latestMs, nowMs, delayMs);
}

/**
 * How stale the newest recorded instant may be for the live edge to still mean anything.
 *
 * Matches the server's own on-air grace (core.onair.GRACE): past it nothing is running, and
 * going live would ask for a playlist with nothing in it.
 */
export const LIVE_EDGE_LAG_MS = 300_000;

/**
 * Whether a gesture asking for `desiredCenterMs` was reaching for the live edge.
 *
 * Measured against the centre the gesture asked for, not against where the clamp left it. The
 * clamp only runs while the pointer is moving, so a pointer held against the stop for a few
 * seconds ends up that far behind the clock, and no tolerance on "how close to now" could tell
 * that apart from a deliberate landing in the recent past. Wanting to go past the stop is the
 * gesture, and expressed that way it needs no tolerance and no scaling with the zoom.
 *
 * The stop is `playableEnd`, not the clock. Everything between the two is the live delay, and
 * a gesture that reaches into it is reaching for the newest audio there is, which is what going
 * live means. Judged against the clock instead, such a gesture would be neither a live landing
 * nor a legal one, and would end where nothing at all happened.
 */
export function landsOnLiveEdge(
  desiredCenterMs: number,
  latestMs: number | null,
  nowMs: number,
  delayMs: number,
): boolean {
  return (
    desiredCenterMs >= playableEnd(latestMs, nowMs, delayMs) &&
    latestMs !== null &&
    nowMs - latestMs < LIVE_EDGE_LAG_MS
  );
}
